import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    DialogContent,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    SelectChangeEvent,
    Slider,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import DialogAdaptive from '../../../common/DialogAdaptive';
import OnlyFansAlbumSelector from '../../../common/OnlyFansAlbumSelector';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';
import useAxios from '../../../../hooks/useAxios';
import useSextforceAutoRepostColors from '../../../../hooks/useSextforceAutoRepostColors';
import useSextforceCaptions from '../../../../hooks/useSextforceCaptions';
import useSextforceAutoStories, { AutoStorySchedule, AutoStorySchedulePayload } from '../../../../hooks/useSextforceAutoStories';
import useSubscriberVaultMedias, { VaultMediaResponseList } from '../../../../hooks/useSubscriberVaultMedias';
import useSubscribersVaultMediasAdditional from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import { SettingsContext } from '../../../../store/SettingsContext';
import { encodeBase64 } from '../../../../utils/common';
import SextforceAutoStoriesEditor, { AutoStoriesEditorValue } from './SextforceAutoStoriesEditor';

type Props = {
    open: boolean;
    schedule: AutoStorySchedule | null;
    onClose: () => void;
};

type RepeatMode = 'off' | 'hours' | 'days';
type ScheduleMode = 'manual' | 'album';

const scheduleModeToInternal = (m: 'MANUAL' | 'ALBUM'): ScheduleMode => (m === 'MANUAL' ? 'manual' : 'album');

const scheduleRepeatUnitToInternal = (unit: 'HOURS' | 'DAYS' | null): RepeatMode => {
    if (unit === 'HOURS') return 'hours';
    if (unit === 'DAYS') return 'days';

    return 'off';
};

// ---------------------------------------------------------------------------
// Lightweight vault photo picker - shows a thumbnail grid of the Subscriber's
// vault photos (optionally filtered by album) and resolves the picked photo
// to { mediaId, previewUrl }. Deliberately does not reuse VaultTile/VaultMenu
// since those pull in editing/download/lightbox features not needed here.
// ---------------------------------------------------------------------------

type VaultPhotoPickerProps = {
    open: boolean;
    onClose: () => void;
    onSelect: (media: { mediaId: number; previewUrl: string; fullUrl: string | null }) => void;
};

const AutoStoriesVaultPhotoPicker = ({ open, onClose, onSelect }: VaultPhotoPickerProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [albumId, setAlbumId] = useState<number>(0);

    const {
        data: vaultMedias,
        isLoading: vaultMediasLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
    } = useSubscriberVaultMedias(
        'sextforceAutoStories',
        albumId,
        OnlyFansVaultMedias.MediaType.Photo,
        null,
        OnlyFansVaultMedias.SortType.Recent,
        OnlyFansVaultMedias.SortOrder.Desc,
        open,
    );

    const photos: VaultMediaResponseList[] = useMemo(() => vaultMedias?.pages.flatMap(page => page.list) || [], [vaultMedias]);

    const handleAlbumChange = (e: SelectChangeEvent<any>) => setAlbumId(Number(e.target.value));

    const handlePick = (photo: VaultMediaResponseList) => {
        // The thumbnail is plenty for the 64px summary preview; the full-size original is
        // only downloaded when the story editor is opened.
        const previewUrl = photo.cachedThumb || photo.thumb || photo.full?.url;

        if (!previewUrl) {
            return;
        }

        onSelect({ mediaId: photo.id, previewUrl, fullUrl: photo.full?.url || null });
    };

    return (
        <DialogAdaptive open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <StyledCardTitleBar title="Choose Base Image" />
            <DialogContent sx={{ p: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <OnlyFansAlbumSelector vaultAlbum={albumId} handleAlbumChange={handleAlbumChange} />
                </Box>

                {vaultMediasLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : photos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 4 }}>
                        No photos found.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1 }}>
                        {photos.map(photo => (
                            <Box
                                key={photo.id}
                                onClick={() => handlePick(photo)}
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    aspectRatio: '1 / 1',
                                    border: 1,
                                    borderColor: 'divider',
                                    '&:hover': { opacity: 0.8 },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={photo.cachedThumb || photo.thumb}
                                    alt=""
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {hasNextPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                            {isFetchingNextPage ? 'Loading…' : 'Load more'}
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <StyledDialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

const SextforceAutoStoriesScheduleDialog = ({ open, schedule, onClose }: Props) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();

    const axios = useAxios();
    const settingsContext = useContext(SettingsContext);
    const params = useParams();

    const { createSchedule, updateSchedule } = useSextforceAutoStories();
    const { data: colors, isLoading: colorsLoading } = useSextforceAutoRepostColors();
    const { categories: captionCategories = [] } = useSextforceCaptions(true);
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditional();

    const isEdit = !!schedule;

    const [name, setName] = useState('');
    const [colorId, setColorId] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<moment.Moment>(moment());
    const [repeatMode, setRepeatMode] = useState<RepeatMode>('off');
    const [repeatAmount, setRepeatAmount] = useState<number>(1);
    const [autoDeleteEnabled, setAutoDeleteEnabled] = useState(false);
    const [autoDeleteHours, setAutoDeleteHours] = useState<number>(6);
    const [mode, setMode] = useState<ScheduleMode>('album');

    const [albumId, setAlbumId] = useState<number>(0);
    const [postImage, setPostImage] = useState(true);
    const [postVideo, setPostVideo] = useState(false);
    const [postGif, setPostGif] = useState(false);
    const [captionCategory, setCaptionCategory] = useState<string | null>(null);

    const [vaultMediaId, setVaultMediaId] = useState<number | null>(null);
    const [baseImagePreviewUrl, setBaseImagePreviewUrl] = useState<string | null>(null);
    // Fresh full-size source URL when known (from an in-session vault pick); resolved lazily
    // via the signed-URL endpoint otherwise.
    const [baseImageFullUrl, setBaseImageFullUrl] = useState<string | null>(null);
    // Object URL of the downloaded full-size original the editor renders from.
    const [editorBaseUrl, setEditorBaseUrl] = useState<string | null>(null);
    const [editorDownloading, setEditorDownloading] = useState(false);
    const editorBlobUrlRef = useRef<string | null>(null);
    const editorBlobMediaIdRef = useRef<number | null>(null);
    const [editorValue, setEditorValue] = useState<AutoStoriesEditorValue | null>(null);

    const [vaultPickerOpen, setVaultPickerOpen] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showAlbumError, setShowAlbumError] = useState(false);

    const prevOpenRef = useRef(false);

    // Reset/populate the form only on the closed -> open transition, mirroring the Story
    // Editor's pattern so a parent re-render mid-edit doesn't clobber in-progress changes.
    useEffect(() => {
        const wasOpen = prevOpenRef.current;
        prevOpenRef.current = open;

        if (!open || wasOpen) {
            return;
        }

        if (schedule) {
            setName(schedule.name);
            setColorId(schedule.colorId);
            setStartDate(moment(schedule.startDate));
            setRepeatMode(scheduleRepeatUnitToInternal(schedule.repeatUnit));
            setRepeatAmount(schedule.repeatAmount || 1);
            setAutoDeleteEnabled(schedule.deleteAfterHours !== null);
            setAutoDeleteHours(schedule.deleteAfterHours ?? 6);
            setMode(scheduleModeToInternal(schedule.mode));
            setAlbumId(schedule.albumId ?? 0);
            setPostImage(schedule.postImage);
            setPostVideo(schedule.postVideo);
            setPostGif(schedule.postGif);
            setCaptionCategory(schedule.captionCategory);
            setVaultMediaId(schedule.vaultMediaId);
            setBaseImagePreviewUrl(null);
            setBaseImageFullUrl(null);
            setEditorBaseUrl(null);
            setEditorValue(
                schedule.canvasWidth && schedule.canvasHeight && schedule.texts
                    ? { canvasWidth: schedule.canvasWidth, canvasHeight: schedule.canvasHeight, crop: schedule.crop, texts: schedule.texts }
                    : null,
            );
        } else {
            setName('');
            setColorId(null);
            setStartDate(moment());
            setRepeatMode('off');
            setRepeatAmount(1);
            setAutoDeleteEnabled(false);
            setAutoDeleteHours(6);
            setMode('album');
            setAlbumId(0);
            setPostImage(true);
            setPostVideo(false);
            setPostGif(false);
            setCaptionCategory(null);
            setVaultMediaId(null);
            setBaseImagePreviewUrl(null);
            setBaseImageFullUrl(null);
            setEditorBaseUrl(null);
            setEditorValue(null);
        }

        setShowAlbumError(false);
    }, [open, schedule]);

    // Resolve the display URL for the already-picked base image when editing a MANUAL
    // schedule - the schedule only stores the vaultMediaId, not a displayable URL. The
    // thumbnail is plenty for the 64px summary preview; the full-size original is only
    // downloaded when the story editor is opened (see handleEditStory).
    useEffect(() => {
        if (!open || !schedule || schedule.mode !== 'MANUAL' || !schedule.vaultMediaId) {
            return;
        }

        let cancelled = false;

        getManyVaultMediaFromDatabase([schedule.vaultMediaId]).then(items => {
            if (cancelled || !items || items.length === 0) {
                return;
            }

            const item = items[0];
            setBaseImagePreviewUrl(item.cachedThumb || item.thumb || item.full?.url || null);
        });

        return () => {
            cancelled = true;
        };
        // Depends on schedule.id only (not the whole schedule object) so a background
        // refetch of the same schedule does not re-trigger the lookup mid-edit.
    }, [open, schedule?.id]);

    // Revoke the downloaded full-size blob whenever the dialog closes and on unmount.
    useEffect(() => {
        if (open) {
            return undefined;
        }

        return () => {
            if (editorBlobUrlRef.current) {
                URL.revokeObjectURL(editorBlobUrlRef.current);
                editorBlobUrlRef.current = null;
                editorBlobMediaIdRef.current = null;
            }
        };
    }, [open]);

    // Downloads the full-size original through the API (exactly like the Vault download
    // button in DownloadManagerContext) and opens the editor on the resulting blob. The
    // preview thumbnail is never used as the editor base: it would render pixelated AND
    // author crop/text coordinates against the wrong image dimensions.
    const handleEditStory = async () => {
        if (!vaultMediaId) {
            return;
        }

        // Reuse the already-downloaded original when re-opening the editor for the same media.
        if (editorBaseUrl && editorBlobMediaIdRef.current === vaultMediaId) {
            setEditorOpen(true);

            return;
        }

        setEditorDownloading(true);

        try {
            // A vault pick in this session carries a fresh full-size URL; in edit mode the
            // cached rows are stale, so resolve a fresh one via the subscriber's OF session.
            let fullUrl = baseImageFullUrl;

            if (!fullUrl) {
                const response = await axios.get(
                    `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaSignedUrl/${vaultMediaId}`,
                );

                fullUrl = response.data?.url || null;
            }

            if (!fullUrl) {
                throw new Error('Could not resolve the full-size media URL');
            }

            const apiUrl = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaFull/${vaultMediaId}?url=${encodeURIComponent(
                encodeBase64(fullUrl),
            )}`;

            const response = await axios.get(apiUrl, { responseType: 'blob', timeout: 0 });

            const blobUrl = URL.createObjectURL(response.data);

            if (editorBlobUrlRef.current) {
                URL.revokeObjectURL(editorBlobUrlRef.current);
            }

            editorBlobUrlRef.current = blobUrl;
            editorBlobMediaIdRef.current = vaultMediaId;
            setEditorBaseUrl(blobUrl);
            setEditorOpen(true);
        } catch (error) {
            console.error('Failed to download the full-size base image', error);
            enqueueSnackbar('Failed to download the full-size base image. Please try again.', { variant: 'error' });
        } finally {
            setEditorDownloading(false);
        }
    };

    const handleColorChange = (e: SelectChangeEvent<string>) => setColorId(e.target.value || null);
    const handleCaptionCategoryChange = (e: SelectChangeEvent<string>) => setCaptionCategory(e.target.value || null);

    const handleAlbumChange = (e: SelectChangeEvent<any>) => {
        const nextAlbumId = Number(e.target.value);

        setAlbumId(nextAlbumId);

        if (nextAlbumId > 0) {
            setShowAlbumError(false);
        }
    };

    const handleVaultPicked = (media: { mediaId: number; previewUrl: string; fullUrl: string | null }) => {
        if (media.mediaId !== vaultMediaId) {
            // A different image invalidates any crop/text positions from the previous image,
            // along with any previously downloaded full-size original.
            setEditorValue(null);

            if (editorBlobUrlRef.current) {
                URL.revokeObjectURL(editorBlobUrlRef.current);
                editorBlobUrlRef.current = null;
                editorBlobMediaIdRef.current = null;
            }

            setEditorBaseUrl(null);
        }

        setVaultMediaId(media.mediaId);
        setBaseImagePreviewUrl(media.previewUrl);
        setBaseImageFullUrl(media.fullUrl);
        setVaultPickerOpen(false);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            enqueueSnackbar('Name is required.', { variant: 'warning' });

            return;
        }

        if (repeatMode !== 'off' && (!repeatAmount || repeatAmount < 1)) {
            enqueueSnackbar('Repeat amount must be at least 1.', { variant: 'warning' });

            return;
        }

        // The backend requires a real album id for album mode (0 = the selector's "All"
        // placeholder, which the worker cannot resolve media from).
        if (mode === 'album' && !(albumId > 0)) {
            setShowAlbumError(true);
            enqueueSnackbar('Please choose an album.', { variant: 'warning' });

            return;
        }

        if (mode === 'album' && !postImage && !postVideo && !postGif) {
            enqueueSnackbar('Select at least one media type for the album.', { variant: 'warning' });

            return;
        }

        if (mode === 'manual' && (!vaultMediaId || !editorValue)) {
            enqueueSnackbar('Choose an image and edit the story before saving.', { variant: 'warning' });

            return;
        }

        const payload: AutoStorySchedulePayload = {
            name: name.trim(),
            mode,
            colorId,
            startDate: startDate.toISOString(),
            repeatAmount: repeatMode === 'off' ? null : repeatAmount,
            repeatUnit: repeatMode === 'off' ? null : repeatMode,
            deleteAfterHours: autoDeleteEnabled ? autoDeleteHours : null,
            active: schedule?.active ?? true,
            // 0 is the selector's "All" placeholder, never a real album - treat it as
            // not-selected. Validation above guarantees album-mode payloads carry a real id.
            albumId: mode === 'album' && albumId > 0 ? albumId : null,
            postImage: mode === 'album' ? postImage : false,
            postVideo: mode === 'album' ? postVideo : false,
            postGif: mode === 'album' ? postGif : false,
            captionCategory: mode === 'album' ? captionCategory : null,
            vaultMediaId: mode === 'manual' ? vaultMediaId : null,
            canvasWidth: mode === 'manual' ? editorValue?.canvasWidth ?? null : null,
            canvasHeight: mode === 'manual' ? editorValue?.canvasHeight ?? null : null,
            crop: mode === 'manual' ? editorValue?.crop ?? null : null,
            texts: mode === 'manual' ? editorValue?.texts ?? null : null,
        };

        setIsSaving(true);

        const result = schedule ? await updateSchedule(schedule.id, payload) : await createSchedule(payload);

        setIsSaving(false);

        if (result) {
            enqueueSnackbar(schedule ? 'Auto Story schedule updated.' : 'Auto Story schedule created.', { variant: 'success' });
            onClose();
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <DialogAdaptive open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} disableEscapeKeyDown>
                <StyledCardTitleBar title={isEdit ? 'Edit Scheduled Story' : 'New Scheduled Story'} />

                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField label="Name" required fullWidth value={name} onChange={e => setName(e.target.value)} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Colour category
                            </Typography>
                            <Select
                                fullWidth
                                size="small"
                                value={colorId ?? ''}
                                onChange={handleColorChange}
                                disabled={colorsLoading}
                                displayEmpty
                            >
                                <MenuItem value="">No category</MenuItem>
                                {(colors || []).map(c => (
                                    <MenuItem key={c._id} value={c._id}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: '50%',
                                                    backgroundColor: c.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span>{c.name}</span>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Start date &amp; time
                            </Typography>
                            <MobileDateTimePicker
                                value={startDate}
                                onChange={value => value && setStartDate(moment(value))}
                                renderInput={params => <TextField {...params} fullWidth size="small" />}
                            />
                        </Grid>

                        <Grid item xs={12} sm={repeatMode === 'off' ? 12 : 6}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Repeat
                            </Typography>
                            <Select fullWidth size="small" value={repeatMode} onChange={e => setRepeatMode(e.target.value as RepeatMode)}>
                                <MenuItem value="off">Off (one-time)</MenuItem>
                                <MenuItem value="hours">Every N hours</MenuItem>
                                <MenuItem value="days">Every N days</MenuItem>
                            </Select>
                        </Grid>
                        {repeatMode !== 'off' && (
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={`Every N ${repeatMode}`}
                                    inputProps={{ min: 1 }}
                                    value={repeatAmount}
                                    onChange={e => setRepeatAmount(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={autoDeleteEnabled} onChange={e => setAutoDeleteEnabled(e.target.checked)} />}
                                label="Auto-delete story after posting"
                            />
                            {autoDeleteEnabled && (
                                <Box sx={{ px: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Delete after {autoDeleteHours} hour{autoDeleteHours === 1 ? '' : 's'}
                                    </Typography>
                                    <Slider
                                        value={autoDeleteHours}
                                        min={1}
                                        max={23}
                                        step={1}
                                        valueLabelDisplay="auto"
                                        onChange={(_e, value) => setAutoDeleteHours(Array.isArray(value) ? value[0] : value)}
                                    />
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Story source
                            </Typography>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                color="primary"
                                value={mode}
                                disabled={isEdit}
                                onChange={(_e, value) => value && setMode(value)}
                            >
                                <ToggleButton value="album">Random from album</ToggleButton>
                                <ToggleButton value="manual">Manual story</ToggleButton>
                            </ToggleButtonGroup>
                            {isEdit && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    The story source cannot be changed after creation.
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            {mode === 'album' ? (
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Album
                                        </Typography>
                                        <OnlyFansAlbumSelector vaultAlbum={albumId} handleAlbumChange={handleAlbumChange} />
                                        {showAlbumError && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                                Please choose an album
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Media types
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            <FormControlLabel
                                                control={<Checkbox checked={postImage} onChange={e => setPostImage(e.target.checked)} />}
                                                label="Photos"
                                            />
                                            <FormControlLabel
                                                control={<Checkbox checked={postVideo} onChange={e => setPostVideo(e.target.checked)} />}
                                                label="Videos"
                                            />
                                            <FormControlLabel
                                                control={<Checkbox checked={postGif} onChange={e => setPostGif(e.target.checked)} />}
                                                label="GIFs"
                                            />
                                        </Stack>
                                        {postVideo && (
                                            <Typography variant="caption" color="text.secondary">
                                                Video picks post silent and truncated to 4 seconds.
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Caption category
                                        </Typography>
                                        <Select fullWidth size="small" value={captionCategory ?? ''} onChange={handleCaptionCategoryChange}>
                                            <MenuItem value="">All captions</MenuItem>
                                            {captionCategories.map(category => (
                                                <MenuItem key={category._id} value={category.name}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Stack>
                            ) : (
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Base image
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            {baseImagePreviewUrl ? (
                                                <Box
                                                    component="img"
                                                    src={baseImagePreviewUrl}
                                                    alt=""
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        objectFit: 'cover',
                                                        borderRadius: 1,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: 1,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <PhotoLibraryIcon color="disabled" />
                                                </Box>
                                            )}
                                            <Stack spacing={1}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<PhotoLibraryIcon />}
                                                    onClick={() => setVaultPickerOpen(true)}
                                                >
                                                    Choose image
                                                </Button>
                                                <LoadingButton
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    disabled={!vaultMediaId}
                                                    loading={editorDownloading}
                                                    onClick={handleEditStory}
                                                >
                                                    <span>Edit story</span>
                                                </LoadingButton>
                                            </Stack>
                                            {editorValue && (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Story configured
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>

                <StyledDialogActions>
                    <Button onClick={onClose} startIcon={<CloseIcon />}>
                        Cancel
                    </Button>
                    <LoadingButton onClick={handleSave} variant="contained" startIcon={<SaveIcon />} loading={isSaving}>
                        <span>Save</span>
                    </LoadingButton>
                </StyledDialogActions>
            </DialogAdaptive>

            <AutoStoriesVaultPhotoPicker open={vaultPickerOpen} onClose={() => setVaultPickerOpen(false)} onSelect={handleVaultPicked} />

            <SextforceAutoStoriesEditor
                open={editorOpen}
                baseImageUrl={editorBaseUrl || ''}
                initialValue={editorValue}
                onSave={value => {
                    setEditorValue(value);
                    setEditorOpen(false);
                }}
                onClose={() => setEditorOpen(false)}
            />
        </LocalizationProvider>
    );
};

export default SextforceAutoStoriesScheduleDialog;
